import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// 페이지 옵션 메뉴(노션 "···") — 디자인 설정(글꼴/작은 텍스트/전체 너비) + 페이지 액션(링크 복사/복제/옮기기/잠금/내보내기).
// IconPicker 와 동일하게 바깥 클릭/ESC 로 닫힌다.
// 백링크/템플릿(마이그레이션 0023)은 PageEditor 수정 없이 이 메뉴에서 자체 처리:
//  - 백링크: 'rt:open-backlinks' CustomEvent 발행 → 상시 마운트된 BacklinksPanel 이 수신.
//  - 템플릿으로 저장: 현재 page_content.ystate 를 workspace.page_templates 로 복사.
const FONTS = [
  { key: 'default', label: '기본', sample: 'Ag' },
  { key: 'serif', label: '세리프', sample: 'Ag', style: { fontFamily: "Georgia, 'Times New Roman', 'Noto Serif KR', serif" } },
  { key: 'mono', label: '모노', sample: 'Ag', style: { fontFamily: "'SFMono-Regular', Consolas, 'D2Coding', monospace" } },
];

// Phase 4 — 번역 대상 언어(ai-assist targetLang 코드 기준).
const TRANSLATE_LANGS = [
  { code: 'en', label: '영어' },
  { code: 'ja', label: '일본어' },
  { code: 'zh', label: '중국어' },
  { code: 'ko', label: '한국어' },
];
// Phase 4 — AI 보조 하위 항목(ai-assist action 기준).
const AI_ACTIONS = [
  { action: 'summarize', label: '요약', icon: '📝' },
  { action: 'continue', label: '이어쓰기', icon: '➕' },
  { action: 'improve', label: '문장 다듬기', icon: '🪄' },
];

export default function PageOptionsMenu({
  settings,
  onChange,
  onClose,
  onCopyLink,
  onDuplicate,
  onMove,
  onToggleLock,
  onExport,
  onVersionHistory,
  onPresent,
  onAnalytics,
  onAiAction,
  onTranslate,
  moveTargets = [],
}) {
  const ref = useRef(null);
  const { pageId } = useParams();
  const { user, membership } = useAuth();
  const font = settings.font || 'default';
  const smallText = !!settings.smallText;
  const fullWidth = !!settings.fullWidth;
  const locked = !!settings.locked;

  // 액션 인라인 상태 — "링크 복사" 피드백, "옮기기" 대상 선택 패널, AI/번역 하위 펼침.
  const [copied, setCopied] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  // "템플릿으로 저장" 진행/완료 피드백
  const [tplBusy, setTplBusy] = useState(false);
  const [tplSaved, setTplSaved] = useState(false);

  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleCopyLink = async () => {
    const ok = await onCopyLink();
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }
  };

  const handleMoveTo = async (parentId) => {
    await onMove(parentId);
    setMoveOpen(false);
    onClose();
  };

  // 백링크 패널 열기 — 이벤트만 발행하고 메뉴는 닫는다 (BacklinksPanel 이 수신).
  const handleBacklinks = () => {
    if (pageId) {
      try {
        window.dispatchEvent(new CustomEvent('rt:open-backlinks', { detail: { pageId } }));
      } catch (e) { /* noop */ }
    }
    onClose();
  };

  // 현재 페이지를 템플릿으로 저장 — page_content.ystate 를 page_templates 행으로 복사.
  // 테이블 미존재(0023 미적용) 시 안내만 하고 종료.
  const handleSaveTemplate = async () => {
    if (!pageId || !membership?.org_id || !user?.id || tplBusy) return;
    const name = window.prompt('템플릿 이름을 입력하세요');
    if (name === null) return; // 취소
    const trimmed = name.trim();
    if (!trimmed) return;
    setTplBusy(true);
    try {
      const [{ data: pc }, { data: pg }] = await Promise.all([
        supabase.schema('workspace').from('page_content')
          .select('ystate').eq('page_id', pageId).maybeSingle(),
        supabase.schema('workspace').from('pages')
          .select('icon').eq('id', pageId).maybeSingle(),
      ]);
      const { error } = await supabase
        .schema('workspace')
        .from('page_templates')
        .insert({
          org_id: membership.org_id,
          name: trimmed,
          icon: (pg && pg.icon) || null,
          ystate_base64: (pc && pc.ystate) || null,
          created_by: user.id,
        });
      if (error) {
        const missing = error.code === '42P01' || error.code === 'PGRST205'
          || /page_templates/i.test(error.message || '');
        window.alert(missing
          ? '페이지 템플릿 기능을 사용하려면 마이그레이션 0023 적용이 필요합니다.'
          : `템플릿 저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
      } else {
        setTplSaved(true);
        setTimeout(() => setTplSaved(false), 1400);
      }
    } catch (e) {
      window.alert('템플릿 저장에 실패했습니다.');
    } finally {
      setTplBusy(false);
    }
  };

  return (
    <div className="pe-popover pe-options-popover" ref={ref} role="dialog" aria-label="페이지 옵션">
      <div className="pe-options-label">글꼴</div>
      <div className="pe-font-row">
        {FONTS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`pe-font-btn${font === f.key ? ' is-active' : ''}`}
            onClick={() => onChange({ font: f.key })}
          >
            <span className="pe-font-sample" style={f.style}>{f.sample}</span>
            <span className="pe-font-name">{f.label}</span>
          </button>
        ))}
      </div>

      <div className="pe-options-row">
        <span className="pe-options-row-label">작은 텍스트</span>
        <button
          type="button"
          className={`pe-toggle${smallText ? ' is-on' : ''}`}
          role="switch"
          aria-checked={smallText}
          aria-label="작은 텍스트"
          onClick={() => onChange({ smallText: !smallText })}
        >
          <span className="pe-toggle-knob" />
        </button>
      </div>

      <div className="pe-options-row">
        <span className="pe-options-row-label">전체 너비</span>
        <button
          type="button"
          className={`pe-toggle${fullWidth ? ' is-on' : ''}`}
          role="switch"
          aria-checked={fullWidth}
          aria-label="전체 너비"
          onClick={() => onChange({ fullWidth: !fullWidth })}
        >
          <span className="pe-toggle-knob" />
        </button>
      </div>

      {/* 페이지 액션 섹션 — 디자인 설정과 구분선으로 분리 */}
      <div className="pe-options-divider" />
      <div className="pe-options-actions">
        <button type="button" className="pe-action-row" onClick={handleCopyLink}>
          <span className="pe-action-icon">🔗</span>
          <span className="pe-action-label">{copied ? '복사됨' : '링크 복사'}</span>
        </button>

        <button type="button" className="pe-action-row" onClick={() => { onDuplicate(); onClose(); }}>
          <span className="pe-action-icon">📄</span>
          <span className="pe-action-label">복제</span>
        </button>

        <button
          type="button"
          className={`pe-action-row${moveOpen ? ' is-open' : ''}`}
          onClick={() => setMoveOpen((o) => !o)}
        >
          <span className="pe-action-icon">➡️</span>
          <span className="pe-action-label">옮기기</span>
          <span className="pe-action-chevron">{moveOpen ? '▾' : '▸'}</span>
        </button>
        {moveOpen ? (
          <div className="pe-move-panel">
            <button type="button" className="pe-move-item" onClick={() => handleMoveTo(null)}>
              <span className="pe-move-item-icon">🏠</span>
              <span className="pe-move-item-label">최상위로</span>
            </button>
            {moveTargets.length === 0 ? (
              <div className="pe-move-empty">이동할 수 있는 페이지가 없습니다.</div>
            ) : (
              moveTargets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="pe-move-item"
                  onClick={() => handleMoveTo(t.id)}
                >
                  <span className="pe-move-item-icon">{t.icon || '📄'}</span>
                  <span className="pe-move-item-label">{t.title || '제목 없음'}</span>
                </button>
              ))
            )}
          </div>
        ) : null}

        <button
          type="button"
          className="pe-action-row"
          onClick={() => onToggleLock(!locked)}
        >
          <span className="pe-action-icon">{locked ? '🔓' : '🔒'}</span>
          <span className="pe-action-label">{locked ? '페이지 잠금 해제' : '페이지 잠금'}</span>
          {locked ? <span className="pe-action-badge">잠김</span> : null}
        </button>

        {onVersionHistory ? (
          <button type="button" className="pe-action-row" onClick={() => { onVersionHistory(); onClose(); }}>
            <span className="pe-action-icon">🕓</span>
            <span className="pe-action-label">버전 기록</span>
          </button>
        ) : null}

        {/* 백링크 / 템플릿 (마이그레이션 0023) — pageId 라우트에서만 노출 */}
        {pageId ? (
          <>
            <button type="button" className="pe-action-row" onClick={handleBacklinks}>
              <span className="pe-action-icon">🔗</span>
              <span className="pe-action-label">백링크</span>
            </button>
            <button
              type="button"
              className="pe-action-row"
              onClick={handleSaveTemplate}
              disabled={tplBusy}
            >
              <span className="pe-action-icon">📑</span>
              <span className="pe-action-label">
                {tplSaved ? '템플릿 저장됨' : (tplBusy ? '템플릿 저장 중…' : '템플릿으로 저장')}
              </span>
            </button>
          </>
        ) : null}

        {/* Phase 4 액션 — 프레젠테이션 / 애널리틱스 / AI / 번역 (구분선으로 분리) */}
        {(onPresent || onAnalytics || onAiAction || onTranslate) ? (
          <div className="pe-options-divider" />
        ) : null}

        {onPresent ? (
          <button type="button" className="pe-action-row" onClick={() => { onPresent(); onClose(); }}>
            <span className="pe-action-icon">🖥️</span>
            <span className="pe-action-label">프레젠테이션 모드</span>
          </button>
        ) : null}

        {onAnalytics ? (
          <button type="button" className="pe-action-row" onClick={() => { onAnalytics(); onClose(); }}>
            <span className="pe-action-icon">📊</span>
            <span className="pe-action-label">업데이트와 애널리틱스</span>
          </button>
        ) : null}

        {onAiAction ? (
          <>
            <button
              type="button"
              className={`pe-action-row${aiOpen ? ' is-open' : ''}`}
              onClick={() => { setAiOpen((o) => !o); setTranslateOpen(false); }}
            >
              <span className="pe-action-icon">✨</span>
              <span className="pe-action-label">AI로 사용</span>
              <span className="pe-action-chevron">{aiOpen ? '▾' : '▸'}</span>
            </button>
            {aiOpen ? (
              <div className="pe-move-panel">
                {AI_ACTIONS.map((a) => (
                  <button
                    key={a.action}
                    type="button"
                    className="pe-move-item"
                    onClick={() => { onAiAction(a.action); onClose(); }}
                  >
                    <span className="pe-move-item-icon">{a.icon}</span>
                    <span className="pe-move-item-label">{a.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}

        {onTranslate ? (
          <>
            <button
              type="button"
              className={`pe-action-row${translateOpen ? ' is-open' : ''}`}
              onClick={() => { setTranslateOpen((o) => !o); setAiOpen(false); }}
            >
              <span className="pe-action-icon">🌐</span>
              <span className="pe-action-label">번역</span>
              <span className="pe-action-chevron">{translateOpen ? '▾' : '▸'}</span>
            </button>
            {translateOpen ? (
              <div className="pe-move-panel">
                {TRANSLATE_LANGS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    className="pe-move-item"
                    onClick={() => { onTranslate(l.code); onClose(); }}
                  >
                    <span className="pe-move-item-icon">🔤</span>
                    <span className="pe-move-item-label">{l.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}

        <div className="pe-options-divider" />
        <button type="button" className="pe-action-row" onClick={() => { onExport(); onClose(); }}>
          <span className="pe-action-icon">⬇️</span>
          <span className="pe-action-label">내보내기 (Markdown)</span>
        </button>
      </div>
    </div>
  );
}
